import { BaseFieldStrategy } from './base-field.strategy';
import { FieldType, Field } from '../models/field.model';
import { KeywordFieldConfig } from '../models/field-type.model';
import { EditorComponent, EditorComponentType } from '../models/editor-component.model';
import { KeywordLookupItem } from '../../../../api/lookup/keyword-lookup-item';

export class KeywordFieldStrategy extends BaseFieldStrategy<KeywordFieldConfig> {
  constructor() {
    super(FieldType.KEYWORD_FIELD, 'Keyword field', '');
  }

  override getDefaultConfig(): KeywordFieldConfig {
    return {
      type: FieldType.KEYWORD_FIELD,
      label: 'Keyword field',
      required: false,
      readOnly: false,
      key: '',
      title: '',
      uiType: null,
      options: null,
      maxLength: null,
      placeholder: '',
      defaultValue: null,
      isWritable: null,
      allowMultiple: false,
      renderType: null,
      mode: undefined,
      choiceDisplayType: 'dropdown',
      layout: 'horizontal',
      createOnly: false,
      isSubField: false,
      hidden: false,
      includeInCopyPaste: true,
      isDropdown: false,
      combinationDisplayNames: [],
    };
  }

  /**
   * Convert keyword data to render type based on uiType, isWritable, allowMultiple, and choice display preferences
   */
  private getRenderTypeFromKeywordData(keywordData?: KeywordLookupItem, config?: KeywordFieldConfig): FieldType | null {
    if (!keywordData) {
      return null;
    }

    // Convert uiType to render type
    switch (keywordData.uiType) {
      case 'Text':
        return FieldType.TEXT;
      case 'Notes':
        return FieldType.TEXTAREA;
      case 'Integer':
      case 'Decimal':
        return FieldType.NUMBER;
      case 'Boolean':
        return FieldType.YES_NO;
      case 'Date Only':
        return FieldType.DATE;
      case 'Country':
        return FieldType.COUNTRY;
      case 'Choice':
        if (keywordData.allowMultiple) {
          // Multiple choice always uses MULTIPLE_CHOICE
          return FieldType.MULTIPLE_CHOICE;
        } else {
          // Single choice: check choiceDisplayType preference
          const displayType = config?.choiceDisplayType || 'dropdown';
          return displayType === 'options' ? FieldType.SINGLE_CHOICE : FieldType.DROPDOWN;
        }
      default:
        return FieldType.TEXT; // Default to text for unknown types
    }
  }

  override createField(id: string, keywordData?: KeywordLookupItem, isSubField?: boolean): Field {
    const config = this.getDefaultConfig();

    if (keywordData) {
      config.key = keywordData.key;
      config.uiType = keywordData.uiType;
      config.maxLength = keywordData.maxLength;
      config.required = Boolean(keywordData.required);
      config.label = keywordData.displayName ?? keywordData.key;
      config.readOnly = !(keywordData.isWritable);
      config.isWritable = keywordData.isWritable;
      config.options = keywordData?.uiType === 'Choice' ? keywordData.options : null;
      config.allowMultiple = keywordData?.allowMultiple ?? false;
      config.renderType = this.getRenderTypeFromKeywordData(keywordData, config);
      config.title = keywordData.title;
      config.defaultValue = keywordData.defaultValue ?? null;
      config.createOnly = keywordData.createOnly ?? false;
      config.isSubField = isSubField ?? false;
      config.hidden = false;
      config.includeInCopyPaste = true;
      if (!isSubField) {
        config.isDropdown = keywordData.isDropdown ?? false;
        config.combinationDisplayNames = keywordData.combinationDisplayNames ?? [];
      }

      // Set mode for number fields based on uiType
      if (keywordData.uiType === 'Integer') {
        config.mode = 'Integer';
      } else if (keywordData.uiType === 'Decimal') {
        config.mode = 'Decimal';
      }
    }

    return {
      ...config,
      id,
      key: config.key || `field_${id}`,
      value: null,
      required: config.required ?? false,
      readOnly: config.readOnly ?? false,
      originalRequired: keywordData?.required ?? false,
      originalIsWritable: keywordData?.isWritable ?? true,
      contextType: keywordData?.contextType,
      ...(isSubField
        ? {}
        : {
            isDropdown: config.isDropdown ?? false,
            combinationDisplayNames: config.combinationDisplayNames ?? [],
          }),
    };
  }

  override getEditorConfig(config?: KeywordFieldConfig): EditorComponent[] {
    const baseConfig: EditorComponent[] = [
      { type: EditorComponentType.TEXT, key: 'label', label: 'Label', columnIndex: 0 },
      { type: EditorComponentType.TEXT, key: 'placeholder', label: 'Placeholder', columnIndex: 0 },
      { type: EditorComponentType.CHECKBOX, key: 'required', label: 'Required', columnIndex: 0 },
      { type: EditorComponentType.CHECKBOX, key: 'readOnly', label: 'Read Only', columnIndex: 0 },
      { type: EditorComponentType.LABEL, key: 'key', label: 'Mapped Keyword', columnIndex: 0 },
      { type: EditorComponentType.LABEL, key: 'title', label: 'Title', columnIndex: 0 },
    ];

    if (config?.isSubField) {
      // For sub-fields, add hidden option
      baseConfig.splice(4, 0, { type: EditorComponentType.CHECKBOX, key: 'hidden', label: 'Hidden', columnIndex: 0 });
      baseConfig.splice(5, 0, { type: EditorComponentType.CHECKBOX, key: 'includeInCopyPaste', label: 'Include in Copy/Paste', columnIndex: 0, defaultValue: true });
    }

    const rightColumnConfig: EditorComponent[] = [
      { type: EditorComponentType.EDITOR, key: 'helpText', label: 'Help Text', columnIndex: 1 },
      { type: EditorComponentType.DEPENDENCY_SELECT, key: 'dependsOn', label: 'Depends On (Yes/No Field)', placeholder: 'Select a Yes/No field...', columnIndex: 1 },
      {
        type: EditorComponentType.SELECT,
        key: 'dependencyAction',
        label: 'Action',
        options: [
          { label: 'Enable when', value: 'enable' },
          { label: 'Hide & Clear when', value: 'hideAndClear' }
        ],
        defaultValue: 'enable',
        columnIndex: 1
      },
      {
        type: EditorComponentType.SELECT,
        key: 'dependencyValue',
        label: 'Value',
        options: [
          { label: 'Yes (True)', value: true },
          { label: 'No (False)', value: false }
        ],
        defaultValue: true,
        columnIndex: 1
      },
    ];

    // Add choice-specific configuration based on renderType
    if (config?.renderType) {
      const renderType = config.renderType;

      // Only show choice-related options for choice field types
      if (renderType === FieldType.SINGLE_CHOICE || renderType === FieldType.DROPDOWN) {
        rightColumnConfig.unshift({
          type: EditorComponentType.SELECT,
          key: 'choiceDisplayType',
          label: 'Display Type',
          options: [
            { label: 'Dropdown', value: 'dropdown' },
            { label: 'Options', value: 'options' }
          ],
          defaultValue: 'dropdown',
          columnIndex: 1
        });
      }

      // Show layout option for choice fields that support it
      if (renderType === FieldType.SINGLE_CHOICE || renderType === FieldType.MULTIPLE_CHOICE || renderType === FieldType.DROPDOWN) {
        rightColumnConfig.unshift({
          type: EditorComponentType.SELECT,
          key: 'layout',
          label: 'Layout',
          options: [
            { label: 'Horizontal', value: 'horizontal' },
            { label: 'Vertical', value: 'vertical' }
          ],
          defaultValue: 'horizontal',
          columnIndex: 1
        });
      }

      // Show number-specific options for numeric fields
      if (renderType === FieldType.NUMBER) {
        baseConfig.splice(2, 0, { // Insert after placeholder
          type: EditorComponentType.SELECT,
          key: 'mode',
          label: 'Number Mode',
          options: [
            { label: 'Integer', value: 'Integer' },
            { label: 'Decimal', value: 'Decimal' }
          ],
          defaultValue: config.mode || 'Integer',
          columnIndex: 0
        });
      }
    }

    return [...baseConfig, ...rightColumnConfig];
  }


  override validateValue(value: any, config: KeywordFieldConfig): boolean {
    // If no uiType specified, accept any value
    if (!config.uiType) {
      return true;
    }

    // Handle null or undefined values - generally allowed unless specifically required
    if (value === null || value === undefined) {
      return true;
    }

    // Validation based on UI type
    switch (config.uiType) {
      case 'Text':
      case 'Notes':
      case 'Country':
        return typeof value === 'string';

      case 'Integer':
        return typeof value === 'number' && Number.isInteger(value);

      case 'Decimal':
        return typeof value === 'number' && !Number.isNaN(value);

      case 'Boolean':
        return typeof value === 'boolean';

      case 'DateOnly':
        // Accept Date objects or valid date strings
        if (value instanceof Date) {
          return !Number.isNaN(value.getTime());
        }
        if (typeof value === 'string') {
          return !Number.isNaN(Date.parse(value));
        }
        return false;

      case 'Choice':
        // Must be string and exist in options array
        if (typeof value !== 'string') {
          return false;
        }
        if (!config.options || !Array.isArray(config.options)) {
          return true; // If no options defined, accept any string
        }
        // Check if value matches any of the option values
        return config.options.some(option => option.value === value);

      case 'Lookup':
        // Lookup fields don't need validation
        return true;

      default:
        // For unknown uiType, accept any value
        return true;
    }
  }
}
